
import {
  RepoLocksRepositoryInterface,
  RepoLocksDto,
} from '@/services/Backup/interfaces';
import { AxiosError } from 'axios';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import Themeable from 'vuetify/lib/mixins/themeable';
import BackupRepoDetailsDialog from '@/components/dialogs/backups/BackupRepoDetailsDialog.vue';
import DeleteBackupRepoDialog from '@/components/dialogs/backups/DeleteBackupRepoDialog.vue';
import PruneBackupRepoDialog from '@/components/dialogs/backups/PruneBackupRepoDialog.vue';
import PageHeader from '@/components/layout/PageHeader.vue';
import {
  LIST_FOOTER_PROPS,
  ListPaginationOptions,
  buildListPaginationQuery,
  defaultListOptions,
  effectivePageSize,
  listPaginationQueryEquals,
  parseListQuery,
} from '@/utils/listPagination';

@Component({
  mixins: [Themeable],
  components: {
    DeleteBackupRepoDialog,
    PruneBackupRepoDialog,
    BackupRepoDetailsDialog,
    PageHeader,
  },
  inject: ['repoLocksRepository'],
})
export default class ActiveBackupRepos extends Vue {
  public loading: boolean = false;
  public options: ListPaginationOptions = defaultListOptions();
  public totalItems: number = 0;
  public LIST_FOOTER_PROPS = LIST_FOOTER_PROPS;

  public repoLocksRepository!: RepoLocksRepositoryInterface;
  public backupRepoLocks: RepoLocksDto[] = [];

  public headers: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Repository Id', value: 'backupRepositoryId' },
    { text: 'Backup Server Id', value: 'backupServerId' },
    { text: 'Lock Type', value: 'type' },
    { text: 'Created At', value: 'createdAt' },
    { text: 'Expires At', value: 'ttl' },
    { text: 'Website IDs', value: 'websiteRestoreLocks' },
    { text: 'Actions', value: 'actions' },
  ];

  public typeLabels = {
    1: 'Forget Task Lock',
    2: 'Prune Task Lock',
    3: 'Backup Task Lock',
    4: 'Restore Task Lock',
    5: 'Terminated Backup Task Lock',
  }

  private ignoreRouteSync = false;
  private applyingRouteQuery = false;

  public applyRouteQuery(): void {
    this.applyingRouteQuery = true;
    try {
      this.options = parseListQuery(this.$route.query);
      this.$nextTick(() => {
        this.applyingRouteQuery = false;
      });
    } catch (err) {
      this.applyingRouteQuery = false;
      throw err;
    }
  }

  public async syncRouteQuery(): Promise<void> {
    if (this.ignoreRouteSync || this.applyingRouteQuery) {
      return;
    }

    const query = buildListPaginationQuery(this.options);
    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }

    this.ignoreRouteSync = true;
    try {
      await this.$router.replace({ name: 'backup_repo_locks', query });
    } catch (err) {
      // Ignore NavigationDuplicated.
    } finally {
      this.$nextTick(() => {
        this.ignoreRouteSync = false;
      });
    }
  }

  public async created(): Promise<void> {
    this.applyRouteQuery();
    await this.initialize();
  }

  public async initialize(): Promise<void> {
    await this.search();
  }

  public async search(): Promise<void> {
    this.loading = true;
    try {
      await this.getBackupRepoLocks();
    } catch (err) {
      await this.onError(err);
    } finally {
      this.loading = false;
    }
  }

  public async getBackupRepoLocks(): Promise<void> {
    const result = await this.repoLocksRepository.searchReposLocks({
      page: this.options.page,
      pageSize: effectivePageSize(this.options.itemsPerPage),
    });
    this.totalItems = result?.total ?? 0;
    this.backupRepoLocks = result?.data ?? [];
  }

  public async onPageChange(): Promise<void> {
    await this.syncRouteQuery();
    await this.search();
  }

  public async onItemsPerPageChange(): Promise<void> {
    this.options.page = 1;
    await this.syncRouteQuery();
    await this.search();
  }

  @Watch('$route')
  public async onRouteChange(): Promise<void> {
    if (this.ignoreRouteSync) {
      return;
    }
    if (this.$route.name !== 'backup_repo_locks') {
      return;
    }
    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }

    this.applyRouteQuery();
    await this.search();
  }

  public async dropLock(id: number): Promise<void> {
    try {
      await this.repoLocksRepository.deleteRepoLock(id);

      this.$successDialog.show('Lock dropped successfully');

      await this.search();
    } catch (err) {
      await this.onError(err);
    }
  }

  private async onError(err: unknown): Promise<void> {
    console.log(err);
    const asAxiosError = err as AxiosError;
    if (asAxiosError.isAxiosError) {
      this.$errorDialog.show(asAxiosError.response?.data?.message ?? 'An axios error occurred');
    } else if (err instanceof Error) {
      this.$errorDialog.show(err.message);
    } else {
      this.$errorDialog.show('An unknown error occurred');
    }
  }
}
